import type React from "react";
import type { WorkflowDocumentsQuery } from "#graphql/supergraphTypes";
import type { WorkflowInstanceRow, TaskStepSummary, TaskStepDetail } from "../types";

export type TaskDocumentAttachment = WorkflowDocumentsQuery["workflowDocuments"][number];

/** Props for the Detail drawer shell */
export type WorkflowDetailProps = {
  workflow: WorkflowInstanceRow | null;
  /** If set, opens directly to this task's detail view (Case 2) */
  activeTaskId: string | null;
  onClose: () => void;
  onTaskIdChange: (taskId: string | null) => void;
  /** workflow_user.id for the logged-in user — used for "is this me?" comparisons
   * against task assignee/maker/checker fields. */
  workflowUserId: string;
};

/** Props for WorkflowHeader */
export type WorkflowHeaderProps = {
  workflow: WorkflowInstanceRow;
  onClose: () => void;
  isGPX?: boolean;
};

/** Props for TaskList (all tasks view) — uses summary data from workflow.taskSteps */
export type TaskListProps = {
  workflow: WorkflowInstanceRow;
  taskSteps: TaskStepSummary[];
  onTaskClick: (taskId: string) => void;
  /** When true (Jira-sourced workflows): hides action chevrons and shows a read-only empty state */
  isReadOnly?: boolean;
  /** Optional node rendered above the Tasks card, inside the same padded
   * container. */
  banner?: React.ReactNode;
  /** CRLT-640 bulk "Assign to me" button in the Tasks card header. Renders
   * only when the callback is provided; parent decides eligibility (all
   * internal tasks unassigned) and gates it out when the client-team
   * auto-assign flag is ON (the UnassignedOwnersBanner supersedes it). */
  onBulkAssignToMe?: () => Promise<void>;
};

/** Props for TaskDetail (single task view) */
export type TaskDetailProps = {
  task: TaskStepDetail;
  onBack: () => void;
  /** workflow_user.id for the logged-in user — used to compare against task.assignee.id */
  workflowUserId: string;
  /** True while a task-level mutation is in flight — locks subtask inputs to prevent races against the pending state refresh. */
  actionLoading?: boolean;
  /** GP-visible documents attached to this task (already filtered by taskId + isExternal). Consumed by TaskDetailGPX only. */
  deliverables?: TaskDocumentAttachment[];
  /** Switches the drawer to the Comments tab and scrolls to the given BTS comment id. Consumed by TaskDetailGPX. */
  onShowComment?: (commentId: string) => void;
};

/** Tabs in the detail drawer */
export enum DetailTabType {
  Tasks = "tasks",
  Communications = "communications",
  Documents = "documents",
  RequestDetails = "request_details",
}
